import { useState } from "react";
import styled from "styled-components";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  Button,
  Checkbox,
  CopyToClipboardButton,
  Icon,
  IconButton,
  Input,
  Modal,
  Stack,
  Text,
} from "@fidelix/fx-miranda";
import { useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { Heading } from "react-aria-components";
import {
  UpdateGraphicPointParams,
  useUpdateScadaGraphicPointMutation,
} from "./graphics.mutation";
import { formatScadaGraphicPointId } from "./graphics.utils";
import { getIsPointErrorState } from "../point/point.utils";
import { getApiErrors } from "../utils/error";
import { HasPermission } from "../utils/permission-helper";
import { Permissions } from "../const";

type PopoverHeaderProps = {
  editMode: boolean;
  name: string;
  onClose: () => void;
  clipBoardContent: string;
  errorState?: number | null;
  scadaEditPointParams?: UpdateGraphicPointParams;
};

type ScadaEditPointForm = {
  updatedPointName: string;
  updatedDeviceId: string;
};

function EditPointModal({
  onClose,
  params,
  onModalClose,
}: {
  onClose: () => void;
  onModalClose: () => void;
  params: UpdateGraphicPointParams;
}) {
  const queryClient = useQueryClient();
  const { companyId, graphic, graphicsId, pointName, deviceId, graphicNodeId } =
    params;
  const hasGraphicNodeId = !!graphicNodeId;
  const [updateAll, setUpdateAll] = useState(!hasGraphicNodeId);
  const { mutate, isPending: isMutating } =
    useUpdateScadaGraphicPointMutation();
  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { isValid, errors },
  } = useForm<ScadaEditPointForm>({
    defaultValues: {
      updatedPointName: pointName,
      updatedDeviceId: deviceId,
    },
    mode: "onChange",
  });

  const onSubmit = (data: ScadaEditPointForm) => {
    clearErrors();
    const pointId = deviceId ? `${deviceId}.${pointName}` : pointName!;
    mutate(
      {
        companyId,
        graphicsId,
        graphic,
        pointId,
        newPointId: formatScadaGraphicPointId(
          data.updatedDeviceId,
          data.updatedPointName,
        ),
        ...(!updateAll && graphicNodeId ? { graphicNodeId } : {}),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["graphics-attachments"],
          });
          queryClient.invalidateQueries({
            queryKey: ["point-gfx"],
          });
          onModalClose();
          onClose();
        },
        onError: (error) => {
          const apiErrors = getApiErrors(error);

          if (apiErrors.DEVICE_NOT_FOUND) {
            setError("updatedDeviceId", {
              type: "server",
              message: t`The device ID is not found`,
            });
          }

          if (apiErrors.POINT_NOT_FOUND) {
            setError("updatedPointName", {
              type: "server",
              message: t`The point name is not found`,
            });
          }
          if (!apiErrors.DEVICE_NOT_FOUND && !apiErrors.POINT_NOT_FOUND) {
            setError("root", {
              type: "server",
              message: t`Unable to update point.`,
            });
          }
        },
      },
    );
  };

  return (
    <Modal
      onClose={onModalClose}
      placement="middle"
      data-test-id="edit-point-modal"
    >
      <EditPointModalTitle slot="title">
        <Text variant="title2Bold">
          <Trans>Edit Point</Trans>
        </Text>
        <IconButton
          icon="close"
          size="small"
          variant="transparent"
          aria-label={t`Close`}
          onPress={onModalClose}
          isDisabled={isMutating}
        />
      </EditPointModalTitle>
      <EditPointModalBody>
        <Controller
          control={control}
          name="updatedPointName"
          rules={{
            required: t`Field is required`,
          }}
          render={({ field, fieldState }) => (
            <FormRow>
              <Text variant="bodyBold">
                <Trans>Point name</Trans>
              </Text>
              <FormInput
                value={field.value}
                onChange={(e) => {
                  clearErrors("root");
                  field.onChange(e);
                }}
                errorMessage={fieldState.error?.message}
              />
            </FormRow>
          )}
        />
        <Controller
          control={control}
          name="updatedDeviceId"
          rules={{
            required: t`Field is required`,
          }}
          render={({ field, fieldState }) => (
            <FormRow>
              <Text variant="bodyBold">
                <Trans>Device ID</Trans>
              </Text>
              <FormInput
                value={field.value}
                onChange={(e) => {
                  clearErrors("root");
                  field.onChange(e);
                }}
                errorMessage={fieldState.error?.message}
              />
            </FormRow>
          )}
        />
        <FormRow>
          <div />
          <CheckboxRow>
            <Checkbox
              isSelected={updateAll}
              onChange={setUpdateAll}
              isDisabled={!hasGraphicNodeId || isMutating}
            />
            <Text variant="body">
              <Trans>
                Update all points in the picture that have the same device ID
                and point name
              </Trans>
            </Text>
          </CheckboxRow>
        </FormRow>
        {errors.root?.message && (
          <ErrorText variant="bodySmall" color="error">
            {errors.root.message}
          </ErrorText>
        )}
      </EditPointModalBody>
      <EditPointModalFooter>
        <Stack axis="x" spacing="medium" justify="end">
          <Button variant="text" onPress={onModalClose} isDisabled={isMutating}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            variant="primary"
            onPress={() => handleSubmit(onSubmit)()}
            isDisabled={!isValid || isMutating}
            isLoading={isMutating}
            iconPlacement="left"
          >
            <Trans>Save</Trans>
          </Button>
        </Stack>
      </EditPointModalFooter>
    </Modal>
  );
}

export function PopoverHeader({
  editMode,
  name,
  onClose,
  clipBoardContent,
  errorState,
  scadaEditPointParams,
}: PopoverHeaderProps) {
  const [editModalOpen, setEditModalOpen] = useState(false);

  return (
    <>
      {editModalOpen && scadaEditPointParams && (
        <EditPointModal
          onClose={onClose}
          params={scadaEditPointParams}
          onModalClose={() => setEditModalOpen(false)}
        />
      )}
      <Heading slot="title">
        <Stack axis="x" spacing="medium" align="center">
          <Text variant="subtitle2Bold">{name}</Text>
          <Stack axis="x" spacing="xxsmall" align="center">
            {editMode && (
              <CopyToClipboardButton
                contentToCopy={clipBoardContent}
                tooltipContent={{
                  onHover: (
                    <Text color="white" variant="bodySmall">
                      <Trans>Copy point name</Trans>
                    </Text>
                  ),
                  onSuccess: (
                    <Text color="white" variant="bodySmall">
                      <Trans>Copied!</Trans>
                    </Text>
                  ),
                }}
              />
            )}
            {editMode && scadaEditPointParams && (
              <HasPermission permission={Permissions.SCADAGRAPHICS_EDIT}>
                <IconButton
                  icon="edit"
                  size="small"
                  variant="transparent"
                  aria-label={t`Edit point`}
                  onPress={() => setEditModalOpen(true)}
                />
              </HasPermission>
            )}
            {errorState != null && getIsPointErrorState(errorState) && (
              <Icon icon="warningFilled" color="error" size={24} />
            )}
          </Stack>
          <PopoverCloseButton onClose={onClose} />
        </Stack>
      </Heading>
    </>
  );
}

const PopoverCloseButton = ({ onClose }: { onClose: () => void }) => (
  <IconButton
    icon="close"
    size="small"
    variant="transparent"
    aria-label="Close popover"
    onPress={onClose}
    style={{ marginLeft: "auto" }}
  />
);

const EditPointModalTitle = styled(Heading)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  margin: 30px;
`;

const EditPointModalBody = styled(Modal.Body)`
  min-width: 600px;
  min-height: 220px;
  display: grid;
  grid-template-columns: 1fr;
  margin: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 100px auto;
  grid-column-gap: ${(p) => p.theme.space("large")};
  align-items: center;
`;

const CheckboxRow = styled.div`
  width: 500px;
  display: flex;
  align-items: flex-start;
  gap: ${(p) => p.theme.space("medium")};
  margin-top: ${(p) => p.theme.space("medium")};
`;

const ErrorText = styled(Text)`
  margin-top: ${(p) => p.theme.space("small")};
  text-align: center;
`;

const FormInput = styled(Input)`
  & > div {
    width: 500px;
  }
`;

const EditPointModalFooter = styled(Modal.Footer)`
  display: flex;
  justify-content: flex-end;
`;
